import { mergeProps } from '@react-aria/utils';
import cn from 'classnames';
import React, { createContext, forwardRef, memo, useCallback, useEffect, useMemo } from 'react';
import { useForwardedRef } from '../../hooks';
import { Box } from '../box';
import styles from './styles.module.css';
export const TabsContext = createContext(null);
export const TabsBase = memo(forwardRef(function TabsBase({ size: initialSize, className, variant, children, ...rest }, ref) {
    const isSegments = variant === 'segments';
    const size = initialSize !== null && initialSize !== void 0 ? initialSize : (isSegments ? 'l' : 'm');
    return (React.createElement(Box, { className: cn(className, styles.tabs, styles[size], {
            [styles.segments]: isSegments,
        }), ref: ref, ...rest },
        React.createElement("div", { className: cn(styles.scroller, styles[size]) }, children)));
}));
export function useTabs(props, ref) {
    const elRef = useForwardedRef(ref);
    return { props: mergeProps(props, { role: 'tablist', 'aria-orientation': 'horizontal' }), elRef };
}
function useTabsScroll(ref, deps) {
    const onResize = useCallback(() => {
        if (!ref || !ref.current) {
            return;
        }
        const rootElem = ref.current;
        const rootElemWidth = rootElem.clientWidth;
        const scrolledElem = rootElem.firstElementChild;
        const scrollElemWidth = (scrolledElem === null || scrolledElem === void 0 ? void 0 : scrolledElem.scrollWidth) || rootElemWidth;
        const scrollPosition = (scrolledElem === null || scrolledElem === void 0 ? void 0 : scrolledElem.scrollLeft) || 0;
        if (scrollElemWidth <= rootElemWidth) {
            return;
        }
        const selectedTab = scrolledElem === null || scrolledElem === void 0 ? void 0 : scrolledElem.querySelector(`[class*="selected_"]`);
        // selected tab should be visible always
        if (selectedTab) {
            const { width: elemWidth } = selectedTab.getBoundingClientRect();
            const tabCenter = selectedTab.offsetLeft + elemWidth / 2;
            if (tabCenter > scrollPosition + rootElemWidth || tabCenter < scrollPosition) {
                scrolledElem === null || scrolledElem === void 0 ? void 0 : scrolledElem.scrollTo({
                    left: tabCenter - rootElemWidth / 2,
                    behavior: 'smooth',
                });
            }
        }
    }, [ref]);
    useEffect(() => {
        const container = ref.current;
        if (!container) {
            return;
        }
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(onResize);
            observer.observe(container);
            return () => observer.unobserve(container);
        }
    }, [onResize]);
    useEffect(() => {
        onResize();
    }, deps);
}
const BaseTabs = memo(forwardRef(function CommonTabs(props, ref) {
    const { props: { onChange, value, ...tabsProps }, elRef, } = useTabs(props, ref);
    const context = useMemo(() => ({
        onChange,
        size: props.size,
        variant: props.variant,
        value,
    }), [onChange, value, props.size]);
    useTabsScroll(elRef, [props.children, props.size, value]);
    return (React.createElement(TabsContext.Provider, { value: context },
        React.createElement(TabsBase, { ...tabsProps, ref: elRef })));
}));
const DefaultTabs = memo(forwardRef(function Tabs(props, ref) {
    return React.createElement(BaseTabs, { ...props, variant: "default", ref: ref });
}));
export const SegmentsTabs = memo(forwardRef(function Tabs(props, ref) {
    return React.createElement(BaseTabs, { ...props, variant: "segments", ref: ref });
}));
export const Tabs = Object.assign(DefaultTabs, { Segments: SegmentsTabs });
